import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, CircleAlert } from 'lucide-react'
import { bannersFor, billingView } from '../../lib/billingStatus'
import { RBAC_MODULES, canAccess, normalizeRole } from '../../lib/rbac'
import { useLangPrefix } from '../../hooks/useLangPrefix'
import useManagerStore from '../../store/managerStore'

const BANNER_STYLE = {
  warning: {
    className: 'border-amber-200 bg-amber-50 text-amber-800',
    Icon: AlertTriangle,
  },
  danger: {
    className: 'border-red-200 bg-red-50 text-red-700',
    Icon: CircleAlert,
  },
}

const ACTION_LABEL = {
  renew: 'billingStatus.renew',
  topup: 'billingStatus.topUp',
}

function BillingBanner({ tone, action, onAction, children }) {
  const { t } = useTranslation()
  const { className, Icon } = BANNER_STYLE[tone]
  return (
    <div className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-sm font-medium ${className}`}>
      <Icon size={18} className="mt-0.5 shrink-0" />
      <span className="flex-1">{children}</span>
      {action && (
        <button type="button" className="shrink-0 underline font-semibold" onClick={onAction}>
          {t(ACTION_LABEL[action])}
        </button>
      )}
    </div>
  )
}

export default function BillingStatusBanners({ className = '' }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { prefix } = useLangPrefix()
  const billingPath = `${prefix}/billing`
  const onBillingPage = useLocation().pathname === billingPath
  const creditStatus = useManagerStore((s) => s.creditStatus)
  const role = useManagerStore((s) => s.role)
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 60 * 1000)
    return () => clearInterval(interval)
  }, [])

  const banners = bannersFor(billingView(creditStatus, new Date(now)), {
    // On the billing page itself the action would lead nowhere.
    canManageBilling: !onBillingPage && canAccess(normalizeRole(role), RBAC_MODULES.billing),
  })
  if (!banners.length) return null

  return (
    <div className={`space-y-2 ${className}`}>
      {banners.map((banner) => (
        <BillingBanner
          key={banner.key}
          tone={banner.tone}
          action={banner.action}
          onAction={() => navigate(billingPath)}
        >
          {t(banner.textKey, banner.params)}
        </BillingBanner>
      ))}
    </div>
  )
}
